// How a project's script roster becomes the module map HollowScript's own
// `resolveImport` reads from. HollowScript resolves `import x from "./util"`
// against the importing file's own path and asks the host for the resolved
// path with the `.hws` extension appended (`"util.hws"`, spec.md §2 - the
// entry file is always `main.hws`, so a flat roster resolves to bare
// `<name>.hws` paths). A project's scripts are a flat roster with no
// folders, so the mapping is exactly one entry per script, keyed by its
// name plus the extension.
//
// Pure and framework-free: the Code workspace, the Preview console, the
// published play surface and the tests all build the same map through
// this one function, so none of them can disagree about what path a
// sibling script is reachable at.

export interface ScriptModuleSource {
  readonly name: string;
  readonly source: string;
}

export const HOLLOWSCRIPT_FILE_EXTENSION = '.hws';

/** The resolved-path key `resolveImport` will ask for when a script imports
 * `./<name>` - `<name>.hws`. */
export function scriptModulePath(name: string): string {
  return `${name}${HOLLOWSCRIPT_FILE_EXTENSION}`;
}

/**
 * Every script in `scripts` except `excludeName` (the entry file itself,
 * which HollowScript already has as `main.hws` - listing it again would let
 * a script import itself under a second name, which is a cycle the
 * language would otherwise reject), keyed by `scriptModulePath`.
 */
export function buildScriptModules(scripts: readonly ScriptModuleSource[], excludeName?: string): Readonly<Record<string, string>> {
  const modules: Record<string, string> = {};
  for (const script of scripts) {
    if (script.name === excludeName) continue;
    modules[scriptModulePath(script.name)] = script.source;
  }
  return modules;
}

/**
 * Whether `name` is usable as a script name: something a sibling script can
 * `import ... from "./<name>"` (spec.md §2 requires a relative path with no
 * extension, so the name must not itself contain a path separator or a
 * dot), a single line, and not empty. Deliberately permissive beyond that -
 * spaces and hyphens are fine, since an import path is a string literal,
 * not an identifier.
 */
export function isValidScriptName(name: string): boolean {
  const trimmed = name.trim();
  if (trimmed.length === 0 || trimmed !== name) return false;
  if (name.length > 80) return false;
  return !/[/\\.\n\r\t"`]/.test(name);
}

/** A name not already in `taken`, derived from `base`: `base`, then
 * `base_2`, `base_3`, ... - the same numbering every reference editor's
 * "New script" uses for an unnamed file. */
export function uniqueScriptName(base: string, taken: ReadonlySet<string>): string {
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base}_${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}
